import parseNumber from "@core-ui/utils/parseNumber";
import t, { type TranslationKey } from "@ext/localization/locale/translate";
import { PopoverInput } from "@ui-kit/Input";
import { useEffect, useState } from "react";
import { ClearPropertyValueButton } from "./ClearPropertyValueButton";
import type { PropertyValueProps } from "./types";

interface InputPropertyValueProps extends PropertyValueProps {
	inputType: "decimal" | "text";
	placeholder: TranslationKey;
}

export const InputPropertyValue = ({
	disabled,
	inputType,
	isAssigned,
	onDelete,
	onSubmit,
	placeholder,
	property,
}: InputPropertyValueProps) => {
	const currentValue = property.value?.[0] ?? "";
	const [value, setValue] = useState(currentValue);

	useEffect(() => {
		setValue(currentValue);
	}, [currentValue]);

	const commit = () => {
		if (value === currentValue) return;
		if (!value.trim()) {
			if (currentValue) onDelete(property.id);
			return;
		}

		const submitted = inputType === "decimal" ? parseNumber(value)?.toString() : value;
		if (submitted === undefined) {
			setValue(currentValue);
			return;
		}
		if (submitted !== currentValue) onSubmit(property.id, submitted);
	};

	const clear = () => {
		setValue("");
		onDelete(property.id);
	};

	return (
		<PopoverInput
			aria-label={property.name}
			className="group lg:h-7 h-7 border-0 bg-transparent px-1.5 text-sm text-foreground [&>input]:cursor-pointer [&>input:focus]:cursor-text [&>input]:pl-0 [&>[data-input-group-element=text]]:pl-1 [&>[data-input-group-element=text]]:pr-0 hover:bg-secondary-bg-hover has-[input:focus]:bg-secondary-bg-hover"
			disabled={disabled}
			endIcon={isAssigned && !disabled && <ClearPropertyValueButton onClear={clear} />}
			inputMode={inputType === "decimal" ? "decimal" : undefined}
			onBlur={commit}
			onChange={(value) => setValue(value ?? "")}
			onKeyDown={(event) => {
				if (event.key === "Enter") event.currentTarget.blur();
				if (event.key === "Escape") {
					event.stopPropagation();
					setValue(currentValue);
					event.currentTarget.blur();
				}
			}}
			placeholder={t(placeholder)}
			type="text"
			value={value}
		/>
	);
};
